import { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Sentry } from './lib/sentry'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ProtectedRoute } from './components/auth/ProtectedRoute'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
})

// Lazy-loaded page components
const LoginPage = lazy(() => import('./pages/auth/LoginPage'))
const SignupPage = lazy(() => import('./pages/auth/SignupPage').then(m => ({ default: m.SignupPage })))
const AcceptInvitePage = lazy(() => import('./pages/auth/AcceptInvitePage'))
const LandingPage = lazy(() => import('./pages/landing/LandingPage'))
const TermsPage = lazy(() => import('./pages/legal/TermsPage'))
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'))
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'))
const InventoryPage = lazy(() => import('./pages/inventory/InventoryPage').then(m => ({ default: m.InventoryPage })))
const LocationsPage = lazy(() => import('./pages/locations/LocationsPage').then(m => ({ default: m.LocationsPage })))
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'))
const IntelligencePage = lazy(() => import('./pages/reports/IntelligencePage'))
const POSSetupPage = lazy(() => import('./pages/settings/POSSetupPage'))
const RecipeConfirmation = lazy(() => import('./components/pos/RecipeConfirmation').then(m => ({ default: m.RecipeConfirmation })))
const TeamPage = lazy(() => import('./pages/team/TeamPage'))
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'))
const ProductsPage = lazy(() => import('./pages/catalog/ProductsPage'))
const ProductDetailPage = lazy(() => import('./pages/catalog/ProductDetailPage'))
const AdminPage = lazy(() => import('./pages/admin/AdminPage'))
const SubscriptionPage = lazy(() => import('./pages/subscription/SubscriptionPage'))

// Layout component
import AppLayout from './components/layout/AppLayout'
import { Toaster } from './components/ui/sonner'

function AppRoutes() {
  const { loading } = useAuth()
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    )
  }
  
  return (
    <Router>
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      }>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/invite" element={<AcceptInvitePage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <AppLayout>
              <DashboardPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/inventory" element={
          <ProtectedRoute requiredRole="company_admin">
            <AppLayout>
              <InventoryPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        {/* Legacy route: redirect old /scan links to Inventory */}
        <Route path="/scan" element={
          <ProtectedRoute>
            <Navigate to="/inventory" replace />
          </ProtectedRoute>
        } />
        
        <Route path="/locations" element={
          <ProtectedRoute requiredRole="staff">
            <AppLayout>
              <LocationsPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/reports" element={
          <ProtectedRoute requiredRole="company_admin">
            <AppLayout>
              <ReportsPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        {/* Legacy route: redirect old variance page to Intelligence */}
        <Route path="/reports/variance" element={
          <ProtectedRoute requiredRole="company_admin">
            <Navigate to="/reports/intelligence" replace />
          </ProtectedRoute>
        } />
        
        <Route path="/reports/intelligence" element={
          <ProtectedRoute requiredRole="company_admin">
            <AppLayout>
              <IntelligencePage />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        {/* Legacy route: redirect old Reconciliation page to Inventory */}
        <Route path="/reconciliation" element={
          <ProtectedRoute requiredRole="company_admin">
            <Navigate to="/inventory" replace />
          </ProtectedRoute>
        } />
        <Route path="/catalog/products" element={
          <ProtectedRoute requiredRole="staff">
            <AppLayout>
              <ProductsPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* Legacy route: redirect old Barcodes page to Products */}
        <Route path="/catalog/barcodes" element={
          <ProtectedRoute requiredRole="company_admin">
            <Navigate to="/catalog/products" replace />
          </ProtectedRoute>
        } />
        <Route path="/catalog/products/:id" element={
          <ProtectedRoute requiredRole="company_admin">
            <AppLayout>
              <ProductDetailPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* Legacy route: redirect old EPC Mappings page to Products */}
        <Route path="/catalog/epc-mappings" element={
          <ProtectedRoute requiredRole="company_admin">
            <Navigate to="/catalog/products" replace />
          </ProtectedRoute>
        } />
        
        
        <Route path="/team" element={
          <ProtectedRoute requiredRole="company_admin">
            <AppLayout>
              <TeamPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/settings" element={
          <ProtectedRoute>
            <AppLayout>
              <SettingsPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/settings/pos-setup" element={
          <ProtectedRoute requiredRole="company_admin">
            <AppLayout>
              <POSSetupPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/settings/pos-setup/confirm" element={
          <ProtectedRoute requiredRole="company_admin">
            <AppLayout>
              <RecipeConfirmation />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/subscription" element={
          <ProtectedRoute requiredRole="company_admin">
            <AppLayout>
              <SubscriptionPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/admin" element={
          <ProtectedRoute requiredRole="super_admin" requireOrganization={false}>
            <AppLayout>
              <AdminPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </Suspense>
    </Router>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Sentry.ErrorBoundary fallback={
        <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
          <div className="text-center space-y-4 p-8">
            <h1 className="text-2xl font-bold text-destructive">Something went wrong</h1>
            <p className="text-muted-foreground">An unexpected error occurred. Please refresh the page.</p>
            <button onClick={() => window.location.reload()} className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90">
              Refresh Page
            </button>
          </div>
        </div>
      }>
        <div className="min-h-screen bg-background text-foreground">
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-right" />
          </AuthProvider>
        </div>
      </Sentry.ErrorBoundary>
    </QueryClientProvider>
  )
}

export default App